import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, RotateCcw, Save, MessageSquare, Clock, ShieldAlert, Mic, Receipt, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const CATEGORY_META = {
  help:      { label: "Help & menu",    icon: HelpCircle },
  auth:      { label: "Access control", icon: ShieldAlert },
  common:    { label: "Common replies", icon: MessageSquare },
  invoice:   { label: "Invoices",       icon: Receipt },
  rate:      { label: "Rate (horse ridden)",  icon: MessageSquare },
  voice:     { label: "Voice notes",    icon: Mic },
  strydom:   { label: "90-day decision",icon: MessageSquare },
  scheduler: { label: "Scheduler",      icon: Clock },
};

export default function WhatsAppEditorPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bot-config/");
      setEntries(res.data || []);
      setDrafts(Object.fromEntries((res.data || []).map((e) => [e.key, e.current])));
    } catch (e) {
      toast.error(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of entries) {
      const cat = e.category || "other";
      if (!g[cat]) g[cat] = [];
      g[cat].push(e);
    }
    return g;
  }, [entries]);

  const onChange = (key, val) => setDrafts((d) => ({ ...d, [key]: val }));

  const onSave = async (entry) => {
    const value = drafts[entry.key];
    if (value === entry.current) return;  // no-op
    setSavingKey(entry.key);
    try {
      await api.patch(`/bot-config/${encodeURIComponent(entry.key)}`, { value });
      toast.success(`Saved`);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const onReset = async (entry) => {
    if (!entry.overridden) return;
    setSavingKey(entry.key);
    try {
      await api.patch(`/bot-config/${encodeURIComponent(entry.key)}`, { value: null });
      toast.success(`Reverted to default`);
      await load();
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
      throw e;
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-editor-page">
      <PageHeader
        title="WhatsApp Editor"
        subtitle="Edit every message the bot sends, plus the 90-day Stydrom scheduler. Changes propagate to the bot within ~30 seconds (cache TTL)."
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading config…
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => {
          const meta = CATEGORY_META[cat] || { label: cat, icon: MessageSquare };
          const Icon = meta.icon;
          return (
            <Card key={cat} className="p-5 rounded-xl" data-testid={`bot-config-section-${cat}`}>
              <div className="flex items-center gap-2 mb-4 border-b pb-3">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium">{meta.label}</h3>
                <span className="text-xs text-muted-foreground">({items.length})</span>
              </div>
              <div className="space-y-5">
                {items.map((e) => {
                  const value = drafts[e.key] ?? "";
                  const dirty = value !== e.current;
                  const isLong = e.type !== "int" && typeof e.default === "string" && e.default.length > 80;
                  return (
                    <div key={e.key} className="space-y-1.5" data-testid={`bot-config-entry-${e.key}`}>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <Label className="text-sm font-medium" htmlFor={`fld-${e.key}`}>
                          {e.label}
                          {e.overridden && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-primary/10 text-primary px-1.5 py-0.5">Overridden</span>
                          )}
                        </Label>
                        <code className="text-[10px] text-muted-foreground">{e.key}</code>
                      </div>
                      {e.type === "int" ? (
                        <Input
                          id={`fld-${e.key}`}
                          type="number"
                          value={value}
                          onChange={(ev) => onChange(e.key, ev.target.value)}
                          className="max-w-[180px] font-mono"
                          data-testid={`bot-config-input-${e.key}`}
                        />
                      ) : isLong ? (
                        <Textarea
                          id={`fld-${e.key}`}
                          rows={Math.min(12, Math.max(3, String(value).split("\n").length + 1))}
                          value={value}
                          onChange={(ev) => onChange(e.key, ev.target.value)}
                          className="font-mono text-xs"
                          data-testid={`bot-config-input-${e.key}`}
                        />
                      ) : (
                        <Input
                          id={`fld-${e.key}`}
                          value={value}
                          onChange={(ev) => onChange(e.key, ev.target.value)}
                          data-testid={`bot-config-input-${e.key}`}
                        />
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          size="sm"
                          variant={dirty ? "default" : "outline"}
                          disabled={!dirty || savingKey === e.key}
                          onClick={() => onSave(e)}
                          data-testid={`bot-config-save-${e.key}`}
                        >
                          {savingKey === e.key
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                            : <Save className="h-3.5 w-3.5 mr-1.5" />}
                          Save
                        </Button>
                        {e.overridden && (
                          <ConfirmDialog
                            title={`Revert "${e.label}" to the factory default?`}
                            description={
                              <>
                                <p>Your customised text for this message will be discarded and the bot will start sending the original built-in wording again within about 30 seconds (cache TTL).</p>
                                <p className="mt-2 text-xs">You can re-customise it later, but the exact text you have now will not be recoverable unless you copy it first.</p>
                              </>
                            }
                            confirmLabel="Yes, revert to default"
                            destructive={false}
                            onConfirm={() => onReset(e)}
                            testId={`bot-config-reset-${e.key}`}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={savingKey === e.key}
                              data-testid={`bot-config-reset-${e.key}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                              Revert to default
                            </Button>
                          </ConfirmDialog>
                        )}
                        {!e.overridden && (
                          <span className="text-[11px] text-muted-foreground">Using factory default</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
